import React, { useEffect, useRef, useState } from 'react';
import { Download, Share2 } from 'lucide-react';
import { downloadDataUrl, qrDataUrl, shareQr } from './qr';

/** One code as `GET users/qr/list` returns it. */
export type QrCodeRow = {
	id: number;
	label: string;
	placement: string | null;
	orgKey: string;
	orgFullName: string;
	authorityId: number | null;
	authorityName: string | null;
	isActive: boolean;
	stale: boolean;
	staleReason: string | null;
	scanCount: number;
	uniqueScanners: number;
	lastScanAt: string | null;
	reportCount: number;
	createdAt: string;
};

// The backend's AllExceptionsFilter sends the message as plain text; Nest's
// default (and ValidationPipe) send JSON. Handle both.
export async function errorMessage(res: Response): Promise<string> {
	try {
		const body = (await res.text()).trim();
		try {
			const data = JSON.parse(body);
			const msg = Array.isArray(data?.message) ? data.message.join(', ') : data?.message;
			if (msg) return msg;
		} catch {
			if (body && body.length < 300 && !body.startsWith('<')) return body;
		}
	} catch {
		/* unreadable body */
	}
	return `Request failed (HTTP ${res.status})`;
}

export const actionBtnCls =
	'inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 h-8 text-xs font-bold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50';

/** Data URL of the QR for `text`, regenerated when it changes. */
export function useQrDataUrl(text: string | null, width = 512): string | null {
	const [dataUrl, setDataUrl] = useState<string | null>(null);
	useEffect(() => {
		if (!text) {
			setDataUrl(null);
			return;
		}
		let cancelled = false;
		qrDataUrl(text, width)
			.then((d) => !cancelled && setDataUrl(d))
			.catch((err) => console.error('QR render failed', err));
		return () => {
			cancelled = true;
		};
	}, [text, width]);
	return dataUrl;
}

/** Width of the saved/shared PNG: big enough to print crisply on a poster. */
const EXPORT_WIDTH = 1024;

/**
 * Download and Share for one saved code. The image is rendered at print size
 * when the button is pressed rather than reusing the small on-screen preview.
 * Share falls back to copying the link where the browser can't share files.
 */
export const QrActions = ({ url, fileName, title }: { url: string; fileName: string; title: string }) => {
	const [busy, setBusy] = useState(false);
	const [note, setNote] = useState<string | null>(null);
	const flash = (msg: string) => {
		setNote(msg);
		setTimeout(() => setNote(null), 2200);
	};

	const run = async (action: (png: string) => Promise<void>) => {
		setBusy(true);
		try {
			await action(await qrDataUrl(url, EXPORT_WIDTH));
		} catch (err) {
			console.error('QR export failed', err);
			flash('Something went wrong');
		} finally {
			setBusy(false);
		}
	};

	return (
		<div className='flex flex-wrap items-center gap-1.5'>
			<button className={actionBtnCls} disabled={busy} onClick={() => run(async (png) => downloadDataUrl(png, fileName))}>
				<Download className='w-3.5 h-3.5' /> Download
			</button>
			<button
				className={actionBtnCls}
				disabled={busy}
				onClick={() =>
					run(async (png) => {
						const result = await shareQr(png, fileName, url, title);
						if (result === 'copied') flash('Sharing not supported here, link copied');
					})
				}
			>
				<Share2 className='w-3.5 h-3.5' /> Share
			</button>
			{note && <span className='text-xs font-semibold text-emerald-700'>{note}</span>}
		</div>
	);
};

/** How often the QR tab refreshes its numbers while it is visible. */
export const LIVE_REFRESH_MS = 15000;

/**
 * Calls `refresh` every LIVE_REFRESH_MS while `enabled` and the browser tab is
 * visible, and once more as soon as the tab becomes visible again. Same
 * approach as the Analytics panel's live view: plain polling, so nothing new
 * is needed on the server or in nginx.
 */
export function useLivePolling(refresh: () => void, enabled: boolean) {
	const latest = useRef(refresh);
	useEffect(() => {
		latest.current = refresh;
	});
	useEffect(() => {
		if (!enabled) return;
		const tick = () => {
			if (document.visibilityState === 'visible') latest.current();
		};
		const id = setInterval(tick, LIVE_REFRESH_MS);
		document.addEventListener('visibilitychange', tick);
		return () => {
			clearInterval(id);
			document.removeEventListener('visibilitychange', tick);
		};
	}, [enabled]);
}

const ago = (ms: number) => {
	const s = Math.max(0, Math.round(ms / 1000));
	if (s < 5) return 'just now';
	if (s < 60) return `${s}s ago`;
	return `${Math.floor(s / 60)}m ago`;
};

/** "Live · updated 5s ago", or an amber note when the last refresh failed. */
export const LiveStatus = ({ updatedAt, failed }: { updatedAt: number | null; failed: boolean }) => {
	const [, setTick] = useState(0);
	useEffect(() => {
		const id = setInterval(() => setTick((n) => n + 1), 1000);
		return () => clearInterval(id);
	}, []);
	if (!updatedAt) return null;
	return failed ? (
		<span className='inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700'>
			<span className='w-2 h-2 rounded-full bg-amber-500' />
			Couldn't refresh · showing data from {ago(Date.now() - updatedAt)}
		</span>
	) : (
		<span className='inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500'>
			<span className='w-2 h-2 rounded-full bg-emerald-500 animate-pulse' />
			Live · updated {ago(Date.now() - updatedAt)}
		</span>
	);
};
